import { RefObject, useEffect } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Reveals every `[data-reveal]` element inside `rootRef` as it scrolls into view
 * (stagger with a `--reveal-delay` inline var). Content stays fully visible
 * without JS, without IntersectionObserver, or with prefers-reduced-motion —
 * the hiding class is only added once we know we can reveal again.
 */
export function useScrollReveal(rootRef: RefObject<HTMLElement>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    if (prefersReducedMotion()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 },
    );

    const observeAll = () => {
      root.querySelectorAll<HTMLElement>('[data-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));
    };
    observeAll();
    root.classList.add('tp-reveal-ready');

    // Sections render async (React Query) — pick up new nodes as they mount.
    const mutations = new MutationObserver(observeAll);
    mutations.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutations.disconnect();
      root.classList.remove('tp-reveal-ready');
    };
  }, [rootRef]);
}
